import { useState, useMemo } from 'react'
import { useAdminSpecialists } from '@/hooks/use-admin-specialists'
import { SpecialistAssignmentCard } from './SpecialistAssignmentCard'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { RefreshCw, Search } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

export function SpecialistManagement() {
  const [searchQuery, setSearchQuery] = useState('')
  const { specialists, isLoading, refresh, isRefreshing } = useAdminSpecialists()

  // Memoize filtered results to prevent unnecessary recalculations
  const filteredAssignments = useMemo(() => {
    if (!searchQuery.trim()) {
      return specialists
    }

    const query = searchQuery.toLowerCase()
    return specialists.filter(specialist => 
      specialist.specialist_name.toLowerCase().includes(query) ||
      specialist.specialist_email.toLowerCase().includes(query) ||
      specialist.caregivers.some(cg => 
        cg.name.toLowerCase().includes(query) ||
        cg.email.toLowerCase().includes(query)
      )
    )
  }, [searchQuery, specialists])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Specialist Assignments</h2>
          <p className="text-muted-foreground">
            Manage caregiver assignments across specialists
          </p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" disabled={isRefreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by specialist or caregiver name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchQuery ? 'No specialists found matching your search' : 'No specialists found'}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAssignments.map(specialist => (
            <SpecialistAssignmentCard
              key={specialist.specialist_id}
              specialist={specialist}
              allSpecialists={specialists}
              onUpdate={refresh}
            />
          ))}
        </div>
      )}
    </div>
  )
}
